// =====================================
// Utility Functions for Focus Management
// =====================================

/**
 * Traps focus inside the cart drawer when it's active.
 * Ensures focus does not move outside of the cart drawer when navigating with Tab or Shift+Tab.
 */

function trapFocusInsideCartDrawer() {
  const cartDrawer = document.querySelector(".cart-drawer");

  document.addEventListener("keydown", (event) => {
    if (
      cartDrawer.classList.contains("cart-drawer--active") &&
      event.key === "Tab"
    ) {
      const focusableElements = cartDrawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey) {
        // If Shift+Tab is pressed
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        // If Tab is pressed
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    }
  });

  document.addEventListener("focusin", (event) => {
    if (
      cartDrawer.classList.contains("cart-drawer--active") &&
      !cartDrawer.contains(event.target)
    ) {
      const focusableElements = cartDrawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      focusableElements[0]?.focus();
    }
  });
}

// =====================================
// Core Cart Drawer Functionality
// =====================================

/**
 * Opens the cart drawer and traps focus inside it.
 * Focus is moved to the close button at the top of the drawer.
 */
function openCartDrawer() {
  const cartDrawer = document.querySelector(".cart-drawer");
  const cartToggle = document.getElementById("cart-drawer-toggle");
  const closeButton = document.querySelector(".cart-drawer-header-right-close");

  cartDrawer.classList.add("cart-drawer--active");
  cartToggle.setAttribute("aria-expanded", "true");

  closeButton?.focus(); // Focus the close button
}

/**
 * Closes the cart drawer and restores focus to the open cart button.
 */
function closeCartDrawer() {
  const cartDrawer = document.querySelector(".cart-drawer");
  const cartToggle = document.getElementById("cart-drawer-toggle");

  cartDrawer.classList.remove("cart-drawer--active");
  cartToggle.setAttribute("aria-expanded", "false");

  cartToggle?.focus(); // Return focus to the open cart button
}

// Function to update cart item counts
function updateCartItemCounts(count) {
  document.querySelectorAll(".cart-count").forEach((el) => {
    el.textContent = count;
    el.style.display = count < 1 ? "none" : "grid";
  });

  // If cart is empty, clear the cart note
  if (count < 1) deleteCartNote();
}

// Function to update the cart drawer with fresh content
async function updateCartDrawer() {
  try {
    const res = await fetch("/?section_id=cart-drawer");
    if (!res.ok) throw new Error("Failed to fetch cart drawer content");

    const text = await res.text();
    const html = document.createElement("div");
    html.innerHTML = text;

    const newBox = html.querySelector(".cart-drawer").innerHTML;
    document.querySelector(".cart-drawer").innerHTML = newBox;

    addCartDrawerListeners(); // Re-attach listeners after updating content
    addCartNoteListeners(); // Re-attach cart note listeners after updating content
  } catch (error) {
    console.error("Error updating cart drawer:", error);
  }
}

// Function to add event listeners to cart drawer elements using event delegation
function addCartDrawerListeners() {
  const cartDrawerBox = document.querySelector(".cart-drawer-box");

  // Use event delegation for remove button clicks
  cartDrawerBox.addEventListener("click", async (event) => {
    if (event.target.closest(".remove-cart-item")) {
      event.preventDefault();

      const rootItem = event.target.closest(".cart-drawer-item");
      const key = rootItem.getAttribute("data-line-item-key");

      // Fetch the current cart data once to determine item quantity and update it
      try {
        const res = await fetch("/cart.js");
        if (!res.ok) throw new Error("Failed to fetch cart data");

        const cart = await res.json();
        const cartItem = cart.items.find((item) => item.key === key);
        if (!cartItem) throw new Error("Item not found in cart");

        const newQuantity = cartItem.quantity - 1;

        // Update the item quantity or remove it if newQuantity is 0
        const updateRes = await fetch("/cart/update.js", {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ updates: { [key]: newQuantity } }),
        });
        const updatedCart = await updateRes.json();

        updateCartItemCounts(updatedCart.item_count); // Update count
        await updateCartDrawer();
      } catch (error) {
        console.error("Error updating item quantity:", error);
      }
    }
  });

  // Stop event propagation on cart drawer box to prevent closing when clicking inside
  cartDrawerBox.addEventListener("click", (e) => e.stopPropagation());

  document
    .querySelectorAll(".cart-drawer-header-right-close, .cart-drawer")
    .forEach((el) => {
      el.addEventListener("click", closeCartDrawer);
    });
}

// Initialize cart drawer listeners
addCartDrawerListeners();

// Add event listener for "Add to Cart" form submissions
document.querySelectorAll('form[action$="/cart/add"]').forEach((form) => {
  form.addEventListener("submit", async (e) => {
    e.preventDefault();

    try {
      await fetch("/cart/add", { method: "POST", body: new FormData(form) });
      const cart = await (await fetch("/cart.js")).json();
      updateCartItemCounts(cart.item_count);
      await updateCartDrawer();
      openCartDrawer();
    } catch (error) {
      console.error("Error adding item to cart:", error);
    }
  });
});

// Add event listener for cart link clicks
document.querySelectorAll('a[href$="/cart"]').forEach((a) => {
  a.addEventListener("click", (e) => {
    e.preventDefault();
    openCartDrawer();
  });
});

// =====================================
// Cart Note Functionality (Non-Intrusive)
// =====================================

// Function to toggle cart note input visibility
function toggleCartNoteInput() {
  const cartNoteWrapper = document.querySelector(".cart-note-input-wrapper");
  const noteOverlay = document.querySelector(".note-overlay");
  const cartNoteInput = document.getElementById("cart-note");

  // Toggle display of cart note input and overlay
  const isVisible = cartNoteWrapper.style.display === "block";
  cartNoteWrapper.style.display = isVisible ? "none" : "block";
  noteOverlay.style.display = isVisible ? "none" : "block";

  // Focus on cart note input if it is now visible
  if (!isVisible) {
    cartNoteInput.focus();
  }
}

// Close overlay on submit or delete
document
  .querySelector(".submit-cart-note-button")
  .addEventListener("click", () => {
    document.querySelector(".note-overlay").style.display = "none";
  });

document
  .querySelector(".delete-cart-note-button")
  .addEventListener("click", () => {
    document.querySelector(".note-overlay").style.display = "none";
  });

// Function to submit the cart note and refresh the drawer if changed
async function submitCartNote() {
  const cartNote = document.getElementById("cart-note").value;
  const loadingSpinner = document.querySelector(".loading-spinner");
  loadingSpinner.style.display = "block"; // Show loading spinner
  document.querySelector(".note-overlay").style.display = "block"; // Ensure overlay stays

  try {
    await fetch("/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ note: cartNote }),
    });
    await updateCartDrawer();
  } catch (error) {
    console.error("Error submitting cart note:", error);
  } finally {
    loadingSpinner.style.display = "none"; // Hide loading spinner
    document.querySelector(".note-overlay").style.display = "none"; // Remove overlay after
  }
}

// Function to delete the cart note and refresh the drawer if necessary
async function deleteCartNote() {
  const noteField = document.getElementById("cart-note");
  noteField.value = ""; // Clear input field for UI
  const loadingSpinner = document.querySelector(".loading-spinner");
  loadingSpinner.style.display = "block"; // Show loading spinner
  document.querySelector(".note-overlay").style.display = "block"; // Ensure overlay stays

  try {
    await fetch("/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ note: "" }),
    });
    await updateCartDrawer();
  } catch (error) {
    console.error("Error deleting cart note:", error);
  } finally {
    loadingSpinner.style.display = "none"; // Hide loading spinner
    document.querySelector(".note-overlay").style.display = "none"; // Remove overlay after
  }
}

// Function to add event listeners for cart note actions
function addCartNoteListeners() {
  const toggleButton = document.querySelector(".cart-note-toggle-button");
  const submitButton = document.querySelector(".submit-cart-note-button");
  const deleteButton = document.querySelector(".delete-cart-note-button");

  if (toggleButton) toggleButton.addEventListener("click", toggleCartNoteInput);
  if (submitButton) submitButton.addEventListener("click", submitCartNote);
  if (deleteButton) deleteButton.addEventListener("click", deleteCartNote);
}

// Initial call to attach cart note listeners
addCartNoteListeners();

// Call focus trapping function
trapFocusInsideCartDrawer();
